/**
 * Pro data API. Two interchangeable implementations behind one `ProApi`
 * contract: the real HTTP client (default) and a localStorage mock for
 * backend-less previews. Select with VITE_API_MODE ("http" | "mock").
 */
import type {
  AddressSuggestion,
  Agent,
  AgendaEntry,
  AgentListing,
  PropertyListing,
  Bid,
  Billing,
  Client,
  ClientFinancials,
  Comment,
  CustomDealTemplate,
  CustomDealTemplateInput,
  Deal,
  DealDetail,
  DealDocument,
  DealStatus,
  DealSummary,
  DealTemplateKey,
  DocumentStatus,
  MemberInvite,
  Membership,
  MortgageProfile,
  MortgageType,
  Organization,
  OrgBranding,
  OrgProfile,
  PlanKey,
  Position,
  ProMe,
  Property,
  ResolvedAddress,
  Role,
  SignatureField,
  SigningRequest,
  Step,
  StepEdge,
  StepStatus,
  StepTypeKey,
  Viewing,
  ViewingStatus,
  Visibility,
} from "@biedradar/shared/pro";
import { httpApi } from "./api.http";
import { mockApi } from "./api.mock";

export interface CreateDealInput {
  clientName: string;
  clientEmail?: string | null;
  clientPhone?: string | null;
  title: string;
  address: string;
  postcode?: string | null;
  askingPrice?: number | null;
  estimatedValue?: number | null;
  lat?: number | null;
  lng?: number | null;
  assigneeUserId?: string | null;
  /** Which built-in starter pipeline to seed (defaults to the buyer journey). */
  templateKey?: DealTemplateKey | null;
  /** An org's custom template to seed from; takes precedence over templateKey. */
  templateId?: string | null;
}

/** Patch for a client's financial profile (all fields optional, null = clear). */
export interface FinancialsInput {
  grossAnnualIncome?: number | null;
  partnerAnnualIncome?: number | null;
  ownFunds?: number | null;
  otherMonthlyIncome?: number | null;
  monthlyObligations?: number | null;
  studentDebt?: number | null;
}

/** Patch for a deal's mortgage profile. */
export interface MortgageInput {
  bankName?: string | null;
  maxMortgage?: number | null;
  interestRateBps?: number | null;
  termYears?: number | null;
  mortgageType?: MortgageType | null;
  nhg?: boolean;
}

/** Patch for the deal's property (address / price / appraised value / coordinates). */
export interface PropertyPatch {
  address?: string;
  askingPrice?: number | null;
  estimatedValue?: number | null;
  postcode?: string | null;
  lat?: number | null;
  lng?: number | null;
}

/** A selling agent (makelaar) on a deal. */
export interface AgentInput {
  name: string;
  agency?: string | null;
  email?: string | null;
  phone?: string | null;
  isPrimary?: boolean;
}

/** Patch for a client's contact details. */
export interface ClientPatch {
  name?: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}

/** A viewed property (the property step's list). */
export interface ViewingInput {
  address: string;
  postcode?: string | null;
  lat?: number | null;
  lng?: number | null;
  viewingDate?: string | null;
  notes?: string | null;
  status?: ViewingStatus;
  agent?: string | null;
  agentId?: string | null;
}

/** Upsert the deal's latest bid. */
export interface BidInput {
  amount: number;
  conditions?: string[];
  status?: string;
}

export interface StepPatch {
  title?: string;
  status?: StepStatus;
  position?: Position;
  records?: Record<string, unknown>;
  visibility?: Visibility;
}

export interface ProApi {
  /** Current advisor identity + memberships + active org (auth-aware). */
  getMe(): Promise<ProMe>;
  /** Create the current user's workspace (onboarding). Requires a session. */
  createOrg(input?: { orgName?: string }): Promise<ProMe>;
  getOrg(): Promise<Organization>;
  getMembers(): Promise<Membership[]>;
  listClients(): Promise<Client[]>;
  updateClient(clientId: string, patch: ClientPatch): Promise<Client>;
  listDeals(): Promise<DealSummary[]>;
  getDeal(id: string): Promise<DealDetail | null>;
  createDeal(input: CreateDealInput): Promise<DealDetail>;
  updateDeal(dealId: string, patch: { status?: DealStatus; title?: string; description?: string | null; assigneeUserId?: string | null }): Promise<Deal>;
  deleteDeal(dealId: string): Promise<void>;
  createStep(dealId: string, typeKey: StepTypeKey, position: Position): Promise<Step>;
  updateStep(stepId: string, patch: StepPatch): Promise<Step>;
  deleteStep(stepId: string): Promise<void>;
  createEdge(dealId: string, source: string, target: string): Promise<StepEdge | null>;
  deleteEdge(edgeId: string): Promise<void>;

  // financing
  updateFinancials(clientId: string, input: FinancialsInput): Promise<ClientFinancials>;
  updateMortgage(dealId: string, input: MortgageInput): Promise<MortgageProfile>;
  updateProperty(dealId: string, patch: PropertyPatch): Promise<Property>;
  upsertBid(dealId: string, input: BidInput): Promise<Bid>;
  addViewing(dealId: string, input: ViewingInput): Promise<Viewing>;
  updateViewing(viewingId: string, patch: Partial<ViewingInput>): Promise<Viewing>;
  deleteViewing(viewingId: string): Promise<void>;
  /** Every viewing across the org's deals, with client/deal context (Agenda). */
  listAgenda(): Promise<AgendaEntry[]>;
  /** Every selling agent across the org's deals (with deal/client context). */
  listOrgAgents(): Promise<AgentListing[]>;
  /** Every property (property step) across the org's deals (with client context). */
  listOrgProperties(): Promise<PropertyListing[]>;
  addAgent(dealId: string, input: AgentInput): Promise<Agent>;
  updateAgent(agentId: string, patch: Partial<AgentInput>): Promise<Agent>;
  deleteAgent(agentId: string): Promise<void>;

  // custom deal templates (org-scoped)
  listTemplates(): Promise<CustomDealTemplate[]>;
  createTemplate(input: CustomDealTemplateInput): Promise<CustomDealTemplate>;
  deleteTemplate(id: string): Promise<void>;

  // org + team
  updateOrg(patch: { name?: string; branding?: OrgBranding | null; profile?: OrgProfile | null }): Promise<Organization>;
  /** Mark the flow/board view-toggle discovery hint as seen (org-wide). */
  dismissViewHint(): Promise<Organization>;
  updateMemberRole(membershipId: string, role: Role): Promise<Membership>;
  removeMember(membershipId: string): Promise<void>;
  inviteMember(email: string, role: Role): Promise<{ invite: MemberInvite; token: string }>;
  listMemberInvites(): Promise<MemberInvite[]>;
  revokeMemberInvite(inviteId: string): Promise<void>;
  acceptInvite(token: string): Promise<{ organizationId: string }>;

  // address autocomplete (Google Places → PDOK fallback; [] / null when offline)
  geoSuggest(q: string): Promise<AddressSuggestion[]>;
  geoResolve(id: string): Promise<ResolvedAddress | null>;

  // collaboration
  requestDocument(
    dealId: string,
    input: { name: string; stepId?: string | null; visibility?: Visibility },
  ): Promise<DealDocument>;
  uploadDocument(
    dealId: string,
    input: {
      name: string;
      mime?: string | null;
      stepId?: string | null;
      documentId?: string | null;
      dataBase64: string;
    },
  ): Promise<DealDocument>;
  setDocumentStatus(docId: string, status: DocumentStatus): Promise<DealDocument>;
  documentFileUrl(docId: string): string;
  addComment(
    dealId: string,
    input: { stepId?: string | null; body: string },
  ): Promise<Comment>;
  createSigningRequest(
    dealId: string,
    input: {
      title: string;
      signerName: string;
      signerEmail?: string | null;
      stepId?: string | null;
      documentId?: string | null;
      /** DocuSign-style placement of the signature box; null = sign in panel. */
      signatureField?: SignatureField | null;
    },
  ): Promise<{ request: SigningRequest; token: string }>;
  createPortalInvite(dealId: string, email: string): Promise<{ token: string }>;
  listAllDocuments(): Promise<DealDocument[]>;

  // billing
  getBilling(): Promise<Billing>;
  /**
   * Start the 7-day free trial WITHOUT a payment method (the onboarding path).
   * Idempotent — one trial per org. Returns the fresh billing snapshot.
   */
  startTrial(plan: PlanKey): Promise<Billing>;
  /**
   * Upgrade to a paid plan: collects the payment method via Mollie hosted
   * checkout. Returns the checkout URL; `checkoutUrl` is null when billing is
   * unavailable (Mollie unconfigured), so the caller can degrade gracefully.
   */
  subscribeBilling(plan: PlanKey): Promise<{ checkoutUrl: string | null }>;
}

const mode = (import.meta.env.VITE_API_MODE as string | undefined) ?? "http";
export const api: ProApi = mode === "mock" ? mockApi : httpApi;
export const API_MODE = mode;
